import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, eur } from "@/lib/apiClient";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CLUSTERS } from "@/lib/constants";
import { Users, Plus, Building2, Search, Loader2, FileText, ClipboardList, ArrowRight, Sparkles, Pencil } from "lucide-react";

const EMPTY = { tipo_ente: "", ragione_sociale: "", codice_fiscale: "", partita_iva: "", indirizzo: "",
  cap: "", comune: "", provincia: "", email: "", pec: "", telefono: "", referente: "", cluster: "", note: "" };

const PRODUCT_NAMES = {
  rc_economia_solidale: "RC Economia Solidale", mrv_volontariato: "Multirischi Volontariato",
  infortuni_economia_solidale: "Infortuni Economia Solidale", cat_nat_impresa: "CAT NAT",
  multirischi_beni: "Multirischi Danni ai Beni", cooperazione_salute: "Cooperazione Salute 4You", d_o: "D&O Directors & Officers",
};

export default function Anagrafiche() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [vatQuery, setVatQuery] = useState("");
  const [looking, setLooking] = useState(false);
  const [detail, setDetail] = useState(null);
  const [pratiche, setPratiche] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const load = () => api.get("/anagrafiche").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const openDetail = async (a) => {
    setDetail(a); setPratiche([]); setLoadingDetail(true);
    try {
      const [{ data: full }, { data: prs }] = await Promise.all([
        api.get(`/anagrafiche/${a.id}`),
        api.get(`/anagrafiche/${a.id}/pratiche`),
      ]);
      setDetail(full); setPratiche(prs);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoadingDetail(false); }
  };

  const lookupVat = async () => {
    const piva = (vatQuery || "").replace(/\D/g, "");
    if (piva.length !== 11) return toast.error("Inserisci una Partita IVA di 11 cifre");
    setLooking(true);
    try {
      const { data } = await api.get(`/lookup/vat/${piva}`);
      setForm((f) => ({
        ...f,
        ragione_sociale: data.ragione_sociale || f.ragione_sociale,
        partita_iva: data.partita_iva || f.partita_iva,
        codice_fiscale: data.codice_fiscale || f.codice_fiscale,
        indirizzo: data.indirizzo || f.indirizzo,
        cap: data.cap || f.cap,
        comune: data.comune || f.comune,
        provincia: data.provincia || f.provincia,
      }));
      toast.success(`Dati recuperati da ${data.fonte}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLooking(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/anagrafiche/${editId}`, form);
        toast.success("Anagrafica aggiornata");
      } else {
        await api.post("/anagrafiche", form);
        toast.success("Anagrafica creata");
      }
      setOpen(false);
      setForm(EMPTY);
      setEditId(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (a) => {
    setForm(Object.fromEntries(Object.keys(EMPTY).map((k) => [k, a[k] ?? ""])));
    setEditId(a.id);
    setDetail(null);
    setOpen(true);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-forest">Le mie anagrafiche</h1>
          <p className="mt-1 text-muted-foreground">Registro clienti isolato, visibile solo a te.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(EMPTY); setVatQuery(""); setEditId(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="new-anagrafica-button" className="gap-2 rounded-xl bg-terracotta hover:bg-terracotta-hover">
              <Plus className="h-4 w-4" /> Nuova anagrafica
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
            <DialogHeader><DialogTitle className="font-heading text-forest">{editId ? "Modifica anagrafica" : "Nuova anagrafica"}</DialogTitle></DialogHeader>
            {form.cluster !== "Persona fisica" && <div className="rounded-xl border border-forest/20 bg-sage/40 p-3">
              <Label className="text-xs font-semibold uppercase tracking-wide text-forest/70">Recupero automatico da Partita IVA</Label>
              <div className="mt-1.5 flex gap-2">
                <Input data-testid="ana-vat-lookup-input" value={vatQuery} placeholder="Es. 02909120921"
                  onChange={(e) => setVatQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && lookupVat()} />
                <Button type="button" data-testid="ana-vat-lookup-button" onClick={lookupVat} disabled={looking}
                  className="shrink-0 gap-2 rounded-lg bg-forest hover:bg-forest-hover">
                  {looking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Recupera dati
                </Button>
              </div>
              <p className="mt-1.5 text-[11px] text-muted-foreground">Fonte ufficiale VIES UE: compila denominazione e indirizzo. PEC, fatturato e dipendenti non sono disponibili gratuitamente.</p>
            </div>}
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label>{form.cluster === "Persona fisica" ? "Nome e cognome *" : "Ragione sociale *"}</Label>
                <Input data-testid="ana-ragione-sociale" value={form.ragione_sociale} onChange={set("ragione_sociale")} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Tipologia</Label>
                  <Select value={form.cluster} onValueChange={(v) => setForm({ ...form, cluster: v, tipo_ente: v })}>
                    <SelectTrigger data-testid="ana-cluster"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>{CLUSTERS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {form.cluster !== "Persona fisica" && <div className="space-y-1.5"><Label>Referente</Label><Input value={form.referente} onChange={set("referente")} data-testid="ana-referente" /></div>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Codice Fiscale</Label><Input value={form.codice_fiscale} onChange={set("codice_fiscale")} data-testid="ana-cf" /></div>
                {form.cluster !== "Persona fisica" && <div className="space-y-1.5"><Label>Partita IVA</Label><Input value={form.partita_iva} onChange={set("partita_iva")} data-testid="ana-piva" /></div>}
              </div>
              <div className="space-y-1.5"><Label>Indirizzo</Label><Input value={form.indirizzo} onChange={set("indirizzo")} data-testid="ana-indirizzo" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5"><Label>CAP</Label><Input value={form.cap} onChange={set("cap")} /></div>
                <div className="space-y-1.5"><Label>Comune</Label><Input value={form.comune} onChange={set("comune")} /></div>
                <div className="space-y-1.5"><Label>Prov.</Label><Input value={form.provincia} onChange={set("provincia")} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Email</Label><Input value={form.email} onChange={set("email")} data-testid="ana-email" /></div>
                <div className="space-y-1.5"><Label>Telefono</Label><Input value={form.telefono} onChange={set("telefono")} data-testid="ana-telefono" /></div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} disabled={saving || !form.ragione_sociale} data-testid="ana-save-button"
                className="bg-terracotta hover:bg-terracotta-hover">Salva anagrafica</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 rounded-xl border-dashed p-12 text-center">
          <Users className="h-10 w-10 text-forest/40" />
          <p className="text-muted-foreground">Nessuna anagrafica ancora. Creane una per iniziare a quotare.</p>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((a) => (
            <Card key={a.id} data-testid={`ana-card-${a.id}`} onClick={() => openDetail(a)}
              className="cursor-pointer rounded-xl border-border/60 p-4 transition-all hover:-translate-y-0.5 hover:border-forest/40 hover:shadow-sm">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sage text-forest">
                  <Building2 className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="truncate font-medium text-forest">{a.ragione_sociale}</div>
                  <div className="text-xs text-muted-foreground">{a.cluster || a.tipo_ente || "Ente"}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{a.comune} {a.provincia && `(${a.provincia})`}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Dettaglio anagrafica arricchito */}
      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) { setDetail(null); setPratiche([]); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="ana-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between gap-3 font-heading text-forest">
                  <span>{detail.ragione_sociale}</span>
                  <Button size="sm" variant="outline" data-testid="ana-edit-button" onClick={() => openEdit(detail)} className="gap-1.5">
                    <Pencil className="h-3.5 w-3.5" /> Modifica
                  </Button>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                  {detail.cluster && <div><span className="text-muted-foreground">Tipologia:</span> <span className="text-forest">{detail.cluster}</span></div>}
                  {detail.codice_fiscale && <div><span className="text-muted-foreground">CF:</span> <span className="text-forest">{detail.codice_fiscale}</span></div>}
                  {detail.partita_iva && <div><span className="text-muted-foreground">P.IVA:</span> <span className="text-forest">{detail.partita_iva}</span></div>}
                  {detail.referente && <div><span className="text-muted-foreground">Referente:</span> <span className="text-forest">{detail.referente}</span></div>}
                  {(detail.comune || detail.provincia) && <div><span className="text-muted-foreground">Sede:</span> <span className="text-forest">{detail.comune} {detail.provincia && `(${detail.provincia})`}</span></div>}
                  {detail.email && <div><span className="text-muted-foreground">Email:</span> <span className="text-forest">{detail.email}</span></div>}
                  {detail.telefono && <div><span className="text-muted-foreground">Tel:</span> <span className="text-forest">{detail.telefono}</span></div>}
                </div>

                {detail.needs_analysis?.suggeriti?.length > 0 && (
                  <div className="rounded-lg border border-forest/20 bg-sage/30 p-3" data-testid="ana-detail-needs">
                    <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-forest/70">
                      <ClipboardList className="h-3.5 w-3.5" /> Ultima analisi esigenze
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {detail.needs_analysis.suggeriti.map((s) => (
                        <span key={s.code} className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${s.level === "consigliato" ? "bg-forest text-white" : "bg-amber-100 text-amber-800"}`}>{s.name}</span>
                      ))}
                    </div>
                    {detail.needs_analysis.updated_at && (
                      <p className="mt-1.5 text-[11px] text-muted-foreground">Compilata il {new Date(detail.needs_analysis.updated_at).toLocaleDateString("it-IT")}</p>
                    )}
                  </div>
                )}

                <div>
                  <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-forest/70">
                    <FileText className="h-3.5 w-3.5" /> Storico pratiche ({pratiche.length})
                  </div>
                  {loadingDetail ? (
                    <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-forest" /></div>
                  ) : pratiche.length === 0 ? (
                    <p className="rounded-lg bg-muted/40 p-3 text-sm text-muted-foreground">Nessuna pratica ancora per questo cliente.</p>
                  ) : (
                    <div className="space-y-2">
                      {pratiche.map((p) => (
                        <button key={p.id} data-testid={`ana-pratica-${p.id}`} onClick={() => navigate(`/preventivi/${p.id}`)}
                          className="flex w-full items-center gap-3 rounded-lg border border-border/60 p-3 text-left transition-colors hover:border-forest/40 hover:bg-sage/20">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 text-sm font-medium text-forest">
                              {p.codice_prev || `#${p.id.slice(-6).toUpperCase()}`}
                              {p.versione && <span className="text-xs text-forest/60">V{p.versione}</span>}
                              {p.proposta_numero && <span className="rounded-full bg-forest px-2 py-0.5 text-[10px] font-medium text-white">{p.proposta_numero}</span>}
                            </div>
                            <div className="truncate text-xs text-muted-foreground">{PRODUCT_NAMES[p.product_code] || p.product_code} · {p.stato}</div>
                          </div>
                          <div className="shrink-0 text-right">
                            <div className="text-sm font-medium text-forest">{eur(p.premio_finale)}</div>
                          </div>
                          <ArrowRight className="h-4 w-4 shrink-0 text-terracotta" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <Button data-testid="ana-detail-nuova-pratica" onClick={() => navigate("/pratica/nuova")}
                  className="w-full gap-2 rounded-xl bg-terracotta hover:bg-terracotta-hover">
                  <Sparkles className="h-4 w-4" /> Nuova pratica per questo cliente
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
